import {
    MoneyMapRuleType,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { orderBy, uniqBy } from 'lodash';

import { type BankAccountDisplayDetail } from '../../banking/types/BankAccount';
import { type Card, CardForm, CardStatus } from '../../banking/types/Card';
import { type User } from '../../user/types/User';

export interface BankAccountAndCard {
    bankAccount?: BankAccountDisplayDetail;
    card?: Card;
}

export const getMoneyMapSpendRuleAccounts = (
    moneyMap: MoneyMapWithRulesAndAccounts | undefined,
    bankAccountDetails: BankAccountDisplayDetail[],
    user: User,
): BankAccountAndCard[] => {
    return getMoneyMapCashAndCardDetailsHelper(
        moneyMap,
        bankAccountDetails,
        user,
        MoneyMapRuleType.SPEND,
    );
};

export const getMoneyMapBillsRuleAccounts = (
    moneyMap: MoneyMapWithRulesAndAccounts | undefined,
    bankAccountDetails: BankAccountDisplayDetail[],
    user: User,
): BankAccountAndCard[] => {
    return getMoneyMapCashAndCardDetailsHelper(
        moneyMap,
        bankAccountDetails,
        user,
        MoneyMapRuleType.BILLS,
    );
};

const getMoneyMapCashAndCardDetailsHelper = (
    moneyMap: MoneyMapWithRulesAndAccounts | undefined,
    bankAccountDetails: BankAccountDisplayDetail[],
    user: User,
    filterType: MoneyMapRuleType,
): BankAccountAndCard[] => {
    // If this data is not loaded yet or is empty, we don't need to process the logic below
    if (bankAccountDetails.length === 0 || moneyMap?.moneyMapRules == null) {
        return [];
    }

    const rulesAccountIds =
        (moneyMap?.moneyMapRules ?? [])
            .filter((r) => r.type === filterType)
            .map((r) => {
                return {
                    priority: r.priority,
                    bankAccountId:
                        r.moneyMapAccount.bankAccountId ??
                        r.moneyMapAccount.moneyMapAccountForTransfer
                            ?.bankAccountId,
                };
            }) ?? [];
    const orderedRulesAccountIds = orderBy(rulesAccountIds, 'priority');

    const uniqueAccountIds = uniqBy(orderedRulesAccountIds, 'bankAccountId');

    const accountsAndCardsToRender = uniqueAccountIds.map(
        ({ bankAccountId }) => {
            const bankAccountDisplayDetail = bankAccountDetails.find(
                (a) => a.bankAccountId === bankAccountId,
            );
            const virtualCardForAccount = bankAccountDisplayDetail?.cards?.find(
                (c) =>
                    c.form === CardForm.VIRTUAL &&
                    c.cardStatus === CardStatus.ACTIVE &&
                    c.syncteraPersonId === user.syncteraPersonId,
            );

            return {
                bankAccount: bankAccountDisplayDetail,
                card: virtualCardForAccount,
            };
        },
    );

    return accountsAndCardsToRender;
};
